import * as React from "react";
import { useEffect } from "react";
import { useQuery } from "@apollo/client";
import { NextMeetingQuery, NextMeetingDocument } from "./queries.generated";
import { LoadingIcon } from "../icons";
import { BackendError } from "@dvrt/graphql-widgets";
import ItemContainer from "./ItemContainer";
import { Empty, SimpleSection } from "../tools";
import { Link, Typography } from "@mui/material";
import { defineMessage, FormattedMessage } from "react-intl";
import moment from "moment";
import Html from "../Html";
import { Link as RouterLink } from "react-router-dom";
import { State } from "../../types";

export type StateProps = Pick<State, "lastMeetingId">;

export default ({ lastMeetingId }: StateProps) => {
    const { data, loading, error, refetch } = useQuery<NextMeetingQuery>(NextMeetingDocument, {
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (lastMeetingId) refetch();
    }, [lastMeetingId]);

    if (loading) return <LoadingIcon />;

    if (error) return <BackendError error={error} />;

    const getDetails = () => {
        const meeting = data.nextMeeting;
        const startTime = moment.parseZone(meeting.startTime).local();
        return (
            <div>
                <SimpleSection title={defineMessage({ id: "meetings.summary", defaultMessage: "Summary" })}>
                    <Typography variant="h6">{meeting.summary}</Typography>
                </SimpleSection>
                <SimpleSection title={defineMessage({ id: "meetings.startTime", defaultMessage: "Start" })}>
                    <Typography variant="h6">
                        {startTime.format("YYYY-MM-DD HH:mm")} ({startTime.fromNow()})
                    </Typography>
                </SimpleSection>
                {Boolean(meeting.description) && (
                    <SimpleSection title={defineMessage({ id: "meetings.description", defaultMessage: "Description" })}>
                        <Html html={meeting.description} />
                    </SimpleSection>
                )}
                {Boolean(meeting.invitations.length) && (
                    <SimpleSection
                        title={defineMessage({ id: "meetings.participants", defaultMessage: "Participants" })}
                    >
                        <ul>
                            {meeting.invitations.map((p) => (
                                <li key={p.id}>
                                    <Typography>{p.name || p.email || p.phoneNumber}</Typography>
                                </li>
                            ))}
                        </ul>
                    </SimpleSection>
                )}
                <SimpleSection title={defineMessage({ id: "meetings.moreDetails", defaultMessage: "More details" })}>
                    <Link component={RouterLink} to={`/meeting/view/${meeting.id}`}>
                        <FormattedMessage id="meeting.detailsLink" defaultMessage="Meeting details page" />
                    </Link>
                </SimpleSection>
            </div>
        );
    };

    return (
        <ItemContainer
            icon="arrow-alt-circle-right"
            label={defineMessage({ id: "meeting.next", defaultMessage: "Next meeting" })}
        >
            {data.nextMeeting ? (
                getDetails()
            ) : (
                <Empty
                    label={defineMessage({ id: "meeting.no_next_meeting", defaultMessage: "There is no next meeting" })}
                />
            )}
        </ItemContainer>
    );
};
